import React from 'react';
import { useSelector } from 'react-redux';
import { ActionMenu, resolveMenuItems } from '@milanote/roma/application';
import { FilePicker } from '@milanote/roma';
import { isGlobalDebugEnabled } from '../../debug/debugUtil';
import { getSelectedElements } from '../../element/selection/selectedElementsSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { isPlatformApple } from '../../platform/utils/platformDetailsUtils';
import { getUsers } from '../../user/usersSelector';
import { getCurrentUserId, userLocalePreferenceSelector } from '../../user/currentUserSelector';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getClipboardElements } from '../shortcuts/clipboard/clipboardSelectors';
import { useTranslator } from '../../components/i18n/useTranslator';
import { useMountSound } from '../../sounds/useMountSound';
import { SoundId } from '../../sounds/soundConstants';
import { elementDesktopMenuConfig } from './configs/elementContextMenuConfig';
import type { ActionMenuConfig } from '@milanote/roma/application';
import type { MenuSize } from '@milanote/roma';
import type { ElementContextMenuContext } from './context/elementContextMenuContext';
import { deriveMenuContext } from './context/deriveMenuContext';
import { useElementContextMenuActions } from './useElementContextMenuActions';
import { resolveElementMetadata } from './items/elementMetadataUtils';
import { isTextClipboardWriteSupported } from '../shortcuts/clipboard/clipboardUtils';

type ElementContextMenuProps = {
    isEditable: boolean;
    currentBoardId?: string;
    menuConfig?: ActionMenuConfig<ElementContextMenuContext>;
    size?: MenuSize;
};

/** Renders the element action registry against the selected elements. */
const ElementContextMenu: React.FC<ElementContextMenuProps> = ({
    isEditable,
    currentBoardId: currentBoardIdOverride,
    menuConfig = elementDesktopMenuConfig,
    size = 'xs',
}) => {
    // Mounts on open (via right click or the toolbar's More tool), so this sounds once per open
    useMountSound(SoundId.PopupOpen);

    const selectedElements = useSelector(getSelectedElements);
    const clipboardElements = useSelector(getClipboardElements);
    const workspaceBoardId = useSelector(getCurrentBoardId);
    const currentBoardId = currentBoardIdOverride ?? workspaceBoardId;
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const users = useSelector(getUsers);
    const currentUserId = useSelector(getCurrentUserId);
    const locale = useSelector(userLocalePreferenceSelector);
    const translate = useTranslator('canvas', 'contextMenuItems') as (localeKey: string) => string;
    const filePicker = FilePicker.useController();
    const actions = useElementContextMenuActions({ isEditable, filePicker, currentBoardId });
    const context: ElementContextMenuContext = {
        ...deriveMenuContext(
            { kind: 'element', currentBoardId, selectedElements },
            { elements: clipboardElements },
            { canWrite: isEditable, showDevTools: isGlobalDebugEnabled() },
            platformDetails,
        ),
        translate,
        actions,
        clipboardInsertSupported: isTextClipboardWriteSupported(platformDetails),
        metadata: resolveElementMetadata(selectedElements),
        users,
        currentUserId,
        locale,
    };
    const nodes = resolveMenuItems(menuConfig, context);
    const platformVariant = isPlatformApple(platformDetails) ? 'apple' : 'nonApple';

    return (
        <ActionMenu.Content
            aria-label="Element actions"
            nodes={nodes}
            autoFocus
            size={size}
            platformVariant={platformVariant}
        />
    );
};

export default ElementContextMenu;
